/* ============================================================
   OrdersPage — TASK-235: customer order list + detail view.
   Surfaces the TASK-234 lifecycle: result photos, confirm /
   dispute gate, tracking, status timeline. Light theme,
   Montserrat, iOS-18 minimal per DESIGN-SYSTEM.md.
   ============================================================ */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { api, resolveMediaUrl } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { formatRub } from '../types/product';
import { OrderTimeline } from './OrderTimeline';
import { OrderChatPanel } from './OrderChatPanel';
import { SlaBadge } from './SlaBadge';
import { CueChip } from './CueChip';
import { buildCustomerOrderView, formatEtaDay } from './customerOrderView';
import { resolveOrdersFocus } from './ordersFocusView';
import { orderColorModeRow, orderColorRow } from './orderColorView';
import { buildShippedTrackingView } from './shippedTrackingView';
import { buildOrderPayView } from './orderPayView';
import type { OrderPayCard } from './orderPayView';
import { RepeatOrderBlock } from './RepeatOrderBlock';
import { CustomerSlicingFiles } from './CustomerSlicingFiles';
import { customerOrderChipsFromCounts, customerOrderFilterEmptyText } from './customerOrderFilter';
import type { CustomerOrderFilter } from './customerOrderFilter';
import { FilterChip } from './FilterChip';
import { ListPager } from './ListPager';
import { chatUnreadLabel } from './orderChatView';
import { Model3DPreview } from './Model3DPreview';
import type { CustomerBanner, CustomerPhotoView } from './customerOrderView';
import { buildOrderActionFailure, plainActionFailure } from './orderActionRecovery';
import type { OrderActionFailureView } from './orderActionRecovery';
import type {
  CounterOffer,
  OrderDetail,
  OrderItem,
  OrderSummary,
  OrderSummaryPage,
  WebOrderStatus,
} from '../types/product';
import { normalizeCounterOfferStatus, normalizeOrderStatus } from '../types/product';
import { productReviewTargets } from './productReviewOrderView';
import type { ProductReviewTarget } from './productReviewOrderView';

const STATUS_LABELS: Record<WebOrderStatus, string> = {
  Pending: 'Ожидает',
  Accepted: 'Принят',
  Printing: 'Печатается',
  Printed: 'Ждёт подтверждения',
  Shipped: 'Отправлен',
  Completed: 'Завершён',
  Cancelled: 'Отменён',
  Disputed: 'Спор',
};

const STATUS_BADGE: Record<WebOrderStatus, string> = {
  Pending: 'badge-warning',
  Accepted: 'badge-warning',
  Printing: 'badge-warning',
  Printed: 'badge-warning',
  Shipped: 'badge-success',
  Completed: 'badge-success',
  Cancelled: 'badge-danger',
  Disputed: 'badge-danger',
};

/** 4171ba09: rows per page for the customer cabinet list. */
const ORDERS_PAGE_SIZE = 10;


export function OrdersPage({ onNeedAuth, focusOrderId = null }: { onNeedAuth: () => void; focusOrderId?: string | null }) {
  const { isAuthenticated } = useAuth();
  // 4171ba09: server-side pagination — one page of slim rows plus the
  // bucket chip counts over the whole history; the cabinet no longer
  // dumps every order into one list.
  const [pageData, setPageData] = useState<OrderSummaryPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // TASK-16aeb9c6 (audit-2 P5): customer status filter — same chip
  // pattern as the provider queue ('all' default shows everything);
  // 4171ba09: applied server-side, so filtering stays truthful under
  // pagination (counts cover the whole history, not just the page).
  const [filter, setFilter] = useState<CustomerOrderFilter>('all');
  const [offset, setOffset] = useState(0);

  const load = useCallback(async (f: CustomerOrderFilter, off: number) => {
    setLoading(true);
    try {
      setPageData(await api.getMyOrdersPage(f, off, ORDERS_PAGE_SIZE));
    } catch {
      setPageData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) void load(filter, offset);
  }, [isAuthenticated, filter, offset, load]);

  const changeFilter = (f: CustomerOrderFilter) => {
    setFilter(f);
    setOffset(0);
  };

  // Detail actions reload the current page (same filter/offset).
  const reload = useCallback(() => { void load(filter, offset); }, [load, filter, offset]);

  const orders = pageData?.items ?? [];

  // s7 (e7fcdcbe): /orders/:id deep link from an order email — once the
  // list has loaded, open the order detail focused on that order. The
  // comparison is case/dash-insensitive (email links carry the canonical
  // lowercase Guid, the API serializes the same way, tolerating copies);
  // an unknown or foreign Guid silently falls back to the plain list.
  // Applied at most once per distinct focus value so it never fights the
  // customer's own navigation after a resolution — but a *new* value (s9
  // notification row tap while already on /orders) still applies.
  const focusApplied = useRef<string | null>(null);
  useEffect(() => {
    if (focusOrderId == null || focusApplied.current === focusOrderId || loading) return;
    focusApplied.current = focusOrderId;
    // d17ac4ce: resolution lives in the pure ordersFocusView helper
    // (unit-tested against the a2da1174 C1-C4 acceptance map). Today
    // only the in-page hit selects; 'direct' (owned order beyond page
    // one, foreign/unknown Guid — API 404s) and 'miss' keep the plain
    // list while 99ba6cff wires the direct-fetch fallback.
    const resolution = resolveOrdersFocus(orders, focusOrderId);
    if (resolution.mode === 'in-page') setSelectedId(resolution.rowId);
  }, [focusOrderId, loading, orders]);

  if (!isAuthenticated) {
    return (
      <div className="container" style={{ paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-2xl)' }}>
        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
          <h2 style={{ fontSize: 'var(--fs-h2)', fontWeight: 'var(--fw-bold)' }}>Мои заказы</h2>
          <p style={{ marginTop: 'var(--space-sm)', color: 'var(--color-text-secondary)' }}>
            Войдите, чтобы видеть свои заказы, фото результата и подтверждать печать.
          </p>
          <button className="btn btn-primary" style={{ marginTop: 'var(--space-lg)' }} onClick={onNeedAuth}>
            Войти
          </button>
        </div>
      </div>
    );
  }

  const selected = orders.find((o) => o.orderId === selectedId) ?? null;

  return (
    <div className="container" style={{ paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-2xl)', display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
      <h2 style={{ fontSize: 'var(--fs-h2)', fontWeight: 'var(--fw-bold)' }}>Мои заказы</h2>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-2xl)' }}>
          <div className="spinner" />
        </div>
      ) : selected ? (
        <OrderDetailView
          orderId={selected.orderId}
          onBack={() => setSelectedId(null)}
          onChanged={reload}
        />
      ) : (pageData?.total ?? 0) === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
          {filter === 'all' ? (
            <>
              <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-primary)', fontWeight: 'var(--fw-semibold)' }}>
                У вас пока нет заказов
              </p>
              <p style={{ marginTop: 'var(--space-xs)', color: 'var(--color-text-secondary)' }}>
                Рассчитайте печать модели в калькуляторе или добавьте товары из каталога — здесь появятся ваши заказы, фото результата и трек-номера.
              </p>
            </>
          ) : (
            <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>
              {customerOrderFilterEmptyText(filter)}
            </p>
          )}
        </div>
      ) : (
        <>
          {/* TASK-16aeb9c6: status filter chips — provider-queue pattern.
              4171ba09: counts now come from the server (whole history), so
              the chips stay truthful under pagination. */}
          <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
            {customerOrderChipsFromCounts(pageData?.bucketCounts ?? {}).map((chip) => (
              <FilterChip
                key={chip.filter}
                active={filter === chip.filter}
                onClick={() => changeFilter(chip.filter)}
                label={chip.label}
              />
            ))}
          </div>
          {/* 4171ba09: slim rows — number, dates, status, counterparty,
              total and the unread-chat badge; everything else (cues, hints,
              actions) lives on the detail page. */}
          <div style={{ display: 'grid', gap: 'var(--space-md)' }}>
            {orders.map((order) => (
              <CustomerOrderRow key={order.orderId} order={order} onOpen={() => setSelectedId(order.orderId)} />
            ))}
          </div>
          <ListPager
            total={pageData?.total ?? 0}
            offset={offset}
            limit={ORDERS_PAGE_SIZE}
            onPrev={() => setOffset(Math.max(0, offset - ORDERS_PAGE_SIZE))}
            onNext={() => setOffset(offset + ORDERS_PAGE_SIZE)}
          />
        </>
      )}
    </div>
  );
}

/* ── 4171ba09: slim list row ─────────────────────────────── */

function CustomerOrderRow({ order, onOpen }: { order: OrderSummary; onOpen: () => void }) {
  const status = normalizeOrderStatus(order.status);
  const unread = order.unreadChatMessages ?? 0;
  // TASK-258: once the customer confirmed, "ждёт подтверждения" reads
  // wrong — the badge says whose court the ball is in.
  const statusLabel = status === 'Printed' && order.customerConfirmedAtUtc != null
    ? 'Ждёт отправки'
    : STATUS_LABELS[status];
  return (
    <button
      className="card"
      style={{ textAlign: 'left', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}
      onClick={onOpen}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          Заказ №{order.orderId.slice(0, 8)} · от {new Date(order.createdAtUtc).toLocaleDateString('ru-RU')}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
          {/* 10fb5d39: unread counterpart chat — same wording/tone as the
              chat panel's badge, so row and thread never disagree. */}
          {unread > 0 && (
            <span className="badge badge-warning">💬 {chatUnreadLabel(unread)}</span>
          )}
          <span className={`badge ${STATUS_BADGE[status]}`}>{statusLabel}</span>
        </span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
        {/* 4171ba09: counterparty line — the provider executing the order. */}
        <span style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-medium)' }}>
          {order.providerName ?? 'Исполнитель уточняется'}
        </span>
        <span style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-bold)', color: 'var(--color-primary)' }}>
          {formatRub(order.totalPrice)}
        </span>
      </div>
      {/* Dates: the second date follows the lifecycle — ETA while work is
          in flight, the completion stamp on terminal rows. */}
      {(order.completedAtUtc || order.estimatedDeliveryUtc) && (
        <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-tertiary)' }}>
          {order.completedAtUtc
            ? `Завершён ${new Date(order.completedAtUtc).toLocaleDateString('ru-RU')}`
            : `Доставка до ${formatEtaDay(order.estimatedDeliveryUtc!)}`}
        </span>
      )}
    </button>
  );
}

/* ── Detail ───────────────────────────────────────────────── */

function OrderDetailView({
  orderId,
  onBack,
  onChanged,
}: {
  orderId: string;
  onBack: () => void;
  onChanged: () => void;
}) {
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  // TASK-b2e2cd38: open photo group + index for the full-size viewer —
  // the customer inspects the real photo before confirming the result.
  const [viewer, setViewer] = useState<{ photos: CustomerPhotoView[]; index: number } | null>(null);

  const load = useCallback(async () => {
    try {
      setOrder(await api.getOrder(orderId));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить заказ');
    }
  }, [orderId]);

  useEffect(() => { void load(); }, [load]);

  if (error) {
    return (
      <div className="card" style={{ padding: 'var(--space-xl)' }}>
        <p style={{ color: 'var(--color-danger)' }}>{error}</p>
        <button className="btn btn-tertiary" style={{ marginTop: 'var(--space-md)' }} onClick={onBack}>
          Назад к списку
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="card" style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-2xl)' }}>
        <div className="spinner" />
      </div>
    );
  }

  const status = normalizeOrderStatus(order.status);
  const kind =
    typeof order.kind === 'number'
      ? (['CustomQuote', 'SellerProducts', 'PerformerCart'] as const)[order.kind] ?? 'SellerProducts'
      : order.kind;
  // MS-4 (GAP-E): the «Цвет» row payload — kind-independent, snapshot-driven.
  const colorRow = orderColorRow(order.printColor) ?? orderColorModeRow(order.printColor, order.printColorMode);
  // TASK-252: everything the customer should see — what-now banner,
  // cue chips, photo groups, result-submitted timestamp — comes from
  // one pure view model (mirrors the provider cards, TASK-253).
  const view = buildCustomerOrderView({
    status: order.status,
    createdAtUtc: order.createdAtUtc,
    customerConfirmedAtUtc: order.customerConfirmedAtUtc,
    completedAtUtc: order.completedAtUtc,
    disputedAtUtc: order.disputedAtUtc ?? null,
    disputeReason: order.disputeReason ?? null,
    disputeResolutionNote: order.disputeResolutionNote ?? null,
    disputeResolvedAtUtc: order.disputeResolvedAtUtc ?? null,
    trackingNumber: order.trackingNumber,
    estimatedDeliveryUtc: order.estimatedDeliveryUtc,
    // audit4-A2 (5fecaf32): P2 confirm-window deadline for the banner cue line.
    confirmDeadlineUtc: order.confirmDeadlineUtc ?? null,
    resultPhotos: order.resultPhotos,
    counterOffers: order.counterOffers,
    // G8-D1: delivery snapshot + kind (charged vs estimate text).
    deliveryMethod: order.deliveryMethod ?? null,
    deliveryCostRub: order.deliveryCostRub ?? null,
    kind: order.kind,
  });
  // G8-D3: carrier detection + method-aware copy from the pure shipped
  // view model (no API change — consumes OrderDetail fields as-is).
  // 31c9ace7: checkout carrier snapshot rides along — it wins over the
  // shape guess for label + «Отследить» deep link.
  const trackingView = buildShippedTrackingView({
    trackingNumber: order.trackingNumber,
    deliveryMethod: order.deliveryMethod,
    deliveryCarrier: order.deliveryCarrier,
  });

  // SD-3 (d36195ae): Safe Deal pay surface — rendered strictly from the
  // backend paymentState (rail off ⇒ null ⇒ nothing renders, the detail
  // keeps exactly today's view). Amounts come from backend fields only.
  const payView = buildOrderPayView({
    paymentState: order.paymentState ?? null,
    totalPriceRub: order.totalPrice,
  });

  return (
    <div className="order-detail-layout">
      {/* 4171ba09: left = full information + available actions; the chat
          moved to the sticky right sidebar (.order-detail-chat, global.css). */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
      {/* Header — TASK-256: wraps on narrow screens (back button + title + badges) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
        <button className="btn btn-tertiary" style={{ height: 40, padding: '8px 16px' }} onClick={onBack}>
          ← Назад
        </button>
        <h3 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>
          Заказ №{order.orderId.slice(0, 8)}
        </h3>
        {/* TASK-258: confirmed Printed says whose court the ball is in now. */}
        <span className={`badge ${STATUS_BADGE[status]}`}>
          {status === 'Printed' && order.customerConfirmedAtUtc != null ? 'Ждёт отправки' : STATUS_LABELS[status]}
        </span>
        {/* TASK-243 (G6): overdue / due-soon indicator.
            TASK-255: method snapshot → pickup stage word «выдача». */}
        <SlaBadge sla={order.sla} deliveryMethod={order.deliveryMethod} />
      </div>

      {/* TASK-252: scannable state chips — photo counts, confirmation,
          negotiation, tracking (mirrors the provider queue cards).
          SD-3: the pay chip rides the same row while the rail is on. */}
      {(view.cues.length > 0 || payView !== null) && (
        <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
          {view.cues.map((cue) => (
            <CueChip key={cue.label} label={cue.label} tone={cue.tone} />
          ))}
          {payView !== null && (
            <CueChip label={payView.cue.label} tone={payView.cue.tone} />
          )}
        </div>
      )}

      {/* TASK-252: the one "what now" card — action-oriented when the ball
          is in the customer's court, an explicit waiting state otherwise. */}
      <BannerCard banner={view.banner} />

      {/* SD-3 (d36195ae): Safe Deal pay/retry card — awaiting orders get the
          redirect CTA (POST /orders/{id}/pay → YooKassa confirmation URL),
          paid orders the escrow-hold confirmation. Hidden while the rail
          is off (payView null). */}
      {payView !== null && (
        <OrderPayBlock
          orderId={order.orderId}
          card={payView.card}
          onChanged={() => { void load(); onChanged(); }}
        />
      )}

      {/* TASK-c82f2eb1 (audit-2 P4): Completed orders close the loop in
          place — the review CTA the banner always promised, pre-bound to
          this order (no re-finding the provider/product). Reuses the
          ReviewsBlock submit path (api.createReview); the review engine
          itself is untouched. */}
      {status === 'Completed' && (
        <ReviewOrderBlock orderId={order.orderId} />
      )}

      {/* 0aa086dd (audit-13 Card A): Completed orders also review the shop
          products they contained — one form per product line, same anchor
          placement as the provider-review CTA above (RC-1b pattern). The
          server re-checks the Completed + contains-product + once-per-order
          gates; the «уже отзывали» case surfaces the server message. */}
      {status === 'Completed' && (
        <ProductReviewOrderBlock orderId={order.orderId} items={order.items} />
      )}

      {/* CB-5 (cabinet-gap-audit §5 P5): repeat a Completed order — re-fill
          the cart with the same items where they can honestly be re-added;
          positions whose model files are gone come back with an explicit
          «файл удалён» fallback per line. Same placement as the review CTA:
          terminal orders only, above the timeline. */}
      {status === 'Completed' && (
        <RepeatOrderBlock orderId={order.orderId} />
      )}

      {/* TASK-b66d8989 (B6-M2): the primary confirm/dispute action renders
          right after the banner — above the timeline and photo groups — so
          it stays on the first screen at 390×844 when result photos exist.
          Gating unchanged: still only `view.action.kind === 'confirmResult'`. */}
      {view.action?.kind === 'confirmResult' && (
        <ConfirmDisputeBlock orderId={order.orderId} onDone={() => { void load(); onChanged(); }} />
      )}

      {/* 61454143: finished print files (provider-prepared gcode.3mf) are
          visible to the customer too — above the timeline, hidden while the
          provider is still preparing (server returns Completed only). */}
      <CustomerSlicingFiles orderId={order.orderId} />

      {/* TASK-251: status timeline with real timestamps + next action */}
      <OrderTimeline
        input={{
          status,
          createdAtUtc: order.createdAtUtc,
          customerConfirmedAtUtc: order.customerConfirmedAtUtc,
          completedAtUtc: order.completedAtUtc,
          disputedAtUtc: order.disputedAtUtc ?? null,
          resultSubmittedAtUtc: view.resultSubmittedAtUtc,
          // TASK-248: G8-D1 snapshot — pickup handoff copy on the timeline.
          deliveryMethod: order.deliveryMethod ?? null,
        }}
      />

      {/* TASK-250/252: in-progress photos shared by the provider (grouped,
          counted and dated by the pure view model). */}
      {view.photos.progress.length > 0 && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          <h4 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>
            Фото процесса · {view.photos.progress.length}
          </h4>
          <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
            Исполнитель делится ходом печати — результат будет опубликован отдельно.
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
            {view.photos.progress.map((photo, i) => (
              <PhotoTile
                key={photo.photoId}
                photo={photo}
                alt="Фото процесса"
                onOpen={() => setViewer({ photos: view.photos.progress, index: i })}
              />
            ))}
          </div>
        </div>
      )}

      {/* Result photos + note (final proof only — kind 0, TASK-250) */}
      {(view.photos.result.length > 0 || order.resultNote) && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          <h4 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>
            Результат печати{view.photos.result.length > 0 ? ` · ${view.photos.result.length}` : ''}
          </h4>
          {order.resultNote && (
            <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>{order.resultNote}</p>
          )}
          <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
            {view.photos.result.map((photo, i) => (
              <PhotoTile
                key={photo.photoId}
                photo={photo}
                alt="Фото результата"
                onOpen={() => setViewer({ photos: view.photos.result, index: i })}
              />
            ))}
          </div>
        </div>
      )}

      {/* TASK-b2e2cd38: full-size photo viewer — inspect the print result
          before confirming; ←/→ walk the group, Esc/backdrop closes. */}
      {viewer && (
        <PhotoLightbox
          photos={viewer.photos}
          index={viewer.index}
          onClose={() => setViewer(null)}
          onStep={(delta) => setViewer((v) =>
            v ? { ...v, index: (v.index + delta + v.photos.length) % v.photos.length } : v)}
        />
      )}

      {/* TASK-252: workflow blocks open only when the view model says the
          action is live — dispute-only once the order is shipped (state
          machine rules); the confirmResult variant moved up after BannerCard
          (TASK-b66d8989, B6-M2). */}
      {view.action?.kind === 'dispute' && (
        <ConfirmDisputeBlock disputeOnly orderId={order.orderId} onDone={() => { void load(); onChanged(); }} />
      )}

      {/* TASK-246: price/deadline negotiation while the order is Pending */}
      {kind === 'CustomQuote' && status === 'Pending' && (order.counterOffers?.length ?? 0) > 0 && (
        <CounterOfferBlock
          orderId={order.orderId}
          offers={order.counterOffers!}
          onDone={() => { void load(); onChanged(); }}
        />
      )}

      {/* TASK-6bdd9dc3 / TASK-240 (audit P1): customer cancel — the pure
          view gates it to Pending/Accepted (OrderService guard parity), so
          the web never advertises a POST the server rejects. Two-step
          confirm keeps the destructive action off a single tap; after
          success the reload renders the Cancelled banner. */}
      {view.canCancel && (
        <CancelOrderBlock orderId={order.orderId} onDone={() => { void load(); onChanged(); }} />
      )}

      {/* audit5-B1a (500f41b1): customer confirm-delivery — while the order is
          Shipped the customer can close the lifecycle themselves instead of
          waiting for the provider's Completed write (the audit-5 B1 deadlock
          also gates reviews on Completed). Two-step confirm mirrors the cancel
          block; after success the reload renders the Completed banner. */}
      {status === 'Shipped' && (
        <ConfirmDeliveryBlock orderId={order.orderId} onDone={() => { void load(); onChanged(); }} />
      )}

      {/* Items */}
      {order.items.length > 0 && (
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
          <h4 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>Состав заказа</h4>
          {order.items.map((item, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--fs-body)' }}>
              <span>
                {item.productTitle} × {item.quantity}
                {/* a1a3b8f4: chosen priced options frozen at checkout */}
                {item.optionsSummary && (
                  <span style={{ display: 'block', fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                    {item.optionsSummary}
                  </span>
                )}
              </span>
              <span style={{ fontWeight: 'var(--fw-medium)' }}>{formatRub(item.lineTotalRub)}</span>
            </div>
          ))}
          {/* G8-D1: delivery cost line in the totals — charged cost plain,
              estimate marked ≈ (view model builds the text). */}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--fs-body)' }}>
            <span style={{ color: 'var(--color-text-secondary)' }}>Доставка</span>
            <span style={{ fontWeight: 'var(--fw-medium)' }}>{view.delivery.text}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 'var(--space-xs)', borderTop: '1px solid var(--color-separator)' }}>
            <span style={{ fontWeight: 'var(--fw-semibold)' }}>Итого</span>
            <span style={{ fontWeight: 'var(--fw-bold)', color: 'var(--color-primary)' }}>{formatRub(order.totalPrice)}</span>
          </div>
        </div>
      )}

      {/* Meta */}
      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
        {/* cc4909b4: interactive 3D preview of the ordered model (three.js,
            raw mesh via /models/{id}/content, tinted with the checkout color
            snapshot); the ME-2 PNG stays the fallback for STEP/no-WebGL. */}
        {kind === 'CustomQuote' && order.modelFileId && (
          <Model3DPreview
            fileId={order.modelFileId}
            format={order.modelFileFormat}
            previewUrl={order.modelPreviewUrl}
            fileName={order.modelFileName}
            colorName={order.printColor}
            alt={`Модель ${order.modelFileName}`}
            height={200}
            style={{ marginBottom: 'var(--space-sm)' }}
          />
        )}
        {order.providerName && <MetaRow label="Исполнитель" value={order.providerName} />}
        {kind === 'CustomQuote' && (
          <>
            <MetaRow label="Модель" value={order.modelFileName} />
            <MetaRow label="Материал" value={order.materialName} />
          </>
        )}
        {/* MS-4 (GAP-E, OPS-3 §65): PrintColor snapshots ride on every print
            order — CustomQuote legs AND PerformerCart cart legs (MS-2
            checkout); null (product-only / legacy rows) keeps the row hidden. */}
        {colorRow != null && <MetaRow label="Цвет" value={colorRow.value} />}
        {kind === 'CustomQuote' && (
          <MetaRow label="Время печати" value={`~${order.estimatedPrintMinutes} мин`} />
        )}
        {/* G8-D3: carrier-labeled tracking number + «Отследить» deep link
            (СДЭК = 10 digits, Почта России = 14 digits — pure
            shippedTrackingView; unknown shapes keep the plain row). */}
        {trackingView.trackingNumber != null && (
          <>
            <MetaRow
              label="Трек-номер"
              value={trackingView.carrierLabel != null
                ? `${trackingView.trackingNumber} · ${trackingView.carrierLabel}`
                : trackingView.trackingNumber}
            />
            {trackingView.trackUrl != null && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <a
                  href={trackingView.trackUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-medium)' }}
                >
                  {trackingView.trackLabel} →
                </a>
              </div>
            )}
          </>
        )}
        {order.shippingAddress && <MetaRow label="Адрес доставки" value={order.shippingAddress} />}
        {/* G8-D1: chosen method + cost snapshot («Курьер — ≈350 ₽» / «Самовывоз — бесплатно»). */}
        <MetaRow label="Доставка" value={view.delivery.text} />
        {order.estimatedDeliveryUtc && (
          <MetaRow label="Ожидаемая доставка" value={formatEtaDay(order.estimatedDeliveryUtc)} />
        )}
        <MetaRow label="Оформлен" value={new Date(order.createdAtUtc).toLocaleString('ru-RU')} />
        {order.notes && <MetaRow label="Комментарий" value={order.notes} />}
      </div>
      </div>
      <aside className="order-detail-chat">
        {/* 33f30c97 (Artem 2026-08-28 §3): order chat — available on every
            status; the backend thread view carries the posting gate and lock
            copy, so the panel never second-guesses the state machine. */}
        <OrderChatPanel orderId={order.orderId} role="customer" />
      </aside>
    </div>
  );
}

/* ── TASK-246: counter-offer negotiation ─────────────────── */

function CounterOfferBlock({
  orderId,
  offers,
  onDone,
}: {
  orderId: string;
  offers: CounterOffer[];
  onDone: () => void;
}) {
  const [mode, setMode] = useState<'idle' | 'counter'>('idle');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = offers.find((o) => normalizeCounterOfferStatus(o.status) === 'Pending') ?? null;
  const last = offers[0]; // newest first — closed history hint

  const respond = async (accept: boolean) => {
    if (!open) return;
    setBusy(true);
    setError(null);
    try {
      if (accept) {
        await api.acceptCounterOffer(orderId, open.offerId);
      } else {
        await api.rejectCounterOffer(orderId, open.offerId);
      }
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось отправить ответ');
    } finally {
      setBusy(false);
    }
  };

  // Customer counters only make sense while a provider offer is open —
  // the backend enforces the same rule.
  if (!open) {
    const closedHint =
      last && normalizeCounterOfferStatus(last.status) === 'Rejected'
        ? 'Предложение исполнителя отклонено — заказ ждёт его решения по текущим условиям.'
        : null;
    return closedHint ? (
      <div className="card" style={{ padding: 'var(--space-md)' }}>
        <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>{closedHint}</p>
      </div>
    ) : null;
  }

  if (!open.isFromProvider) {
    return (
      <div className="card" style={{ padding: 'var(--space-md)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
        <span style={{ fontSize: 'var(--fs-h3)' }}>⏳</span>
        <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-primary)' }}>
          Вы предложили свои условия ({formatRub(open.proposedPriceRub)}) — ждём ответа исполнителя.
        </p>
      </div>
    );
  }

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      <h4 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>Предложение исполнителя</h4>
      <div style={{ display: 'flex', gap: 'var(--space-lg)', flexWrap: 'wrap', alignItems: 'baseline' }}>
        <span style={{ fontSize: 'var(--fs-h2)', fontWeight: 'var(--fw-bold)', color: 'var(--color-primary)' }}>
          {formatRub(open.proposedPriceRub)}
        </span>
        {open.proposedDeliveryUtc && (
          <span style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>
            Доставка до {new Date(open.proposedDeliveryUtc).toLocaleDateString('ru-RU')}
          </span>
        )}
      </div>
      <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>{open.message}</p>
      <p style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-tertiary)' }}>
        Предложено {new Date(open.createdAtUtc).toLocaleString('ru-RU')}
      </p>

      {mode === 'idle' ? (
        <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" disabled={busy} onClick={() => void respond(true)}>
            {busy ? 'Принятие…' : 'Принять условия'}
          </button>
          <button className="btn btn-tertiary" disabled={busy} onClick={() => void respond(false)}>
            Отклонить
          </button>
          <button className="btn btn-secondary" disabled={busy} onClick={() => { setMode('counter'); setError(null); }}>
            Предложить свои условия
          </button>
        </div>
      ) : (
        <CounterOfferForm
          orderId={orderId}
          title="Встречное предложение"
          submitLabel="Отправить предложение"
          onDone={onDone}
          onCancel={() => setMode('idle')}
        />
      )}
      {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}
    </div>
  );
}

/* ── Counter form (shared shape, TASK-246) ───────────────── */

export function CounterOfferForm({
  orderId,
  title,
  submitLabel,
  onDone,
  onCancel,
}: {
  orderId: string;
  title: string;
  submitLabel: string;
  onDone: () => void;
  onCancel?: () => void;
}) {
  const [price, setPrice] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const value = Number(price.replace(',', '.'));
    if (!Number.isFinite(value) || value <= 0) {
      setError('Укажите корректную цену');
      return;
    }
    const text = message.trim();
    if (!text) {
      setError('Добавьте пояснение к предложению');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.createCounterOffer(
        orderId,
        value,
        deliveryDate ? new Date(`${deliveryDate}T00:00:00Z`).toISOString() : null,
        text,
      );
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось отправить предложение');
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    padding: '10px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-separator)',
    fontSize: 'var(--fs-body)',
    fontFamily: 'inherit',
    background: 'var(--color-surface)',
    color: 'var(--color-text-primary)',
  } as const;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
      <h4 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>{title}</h4>
      <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
        <input
          style={{ ...inputStyle, flex: '1 1 180px' }}
          placeholder="Цена, ₽ (например 5500)"
          inputMode="decimal"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />
        <input
          style={{ ...inputStyle, flex: '0 1 190px' }}
          type="date"
          title="Предлагаемая дата доставки"
          value={deliveryDate}
          onChange={(e) => setDeliveryDate(e.target.value)}
        />
      </div>
      <textarea
        style={{ ...inputStyle, resize: 'vertical' }}
        placeholder="Пояснение (обязательно): почему эта цена или срок"
        rows={2}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
      />
      {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}
      <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
        <button className="btn btn-primary" disabled={busy} onClick={() => void submit()}>
          {busy ? 'Отправка…' : submitLabel}
        </button>
        {onCancel && (
          <button className="btn btn-tertiary" disabled={busy} onClick={onCancel}>
            Отмена
          </button>
        )}
      </div>
    </div>
  );
}

/* ── TASK-6bdd9dc3: customer cancel with confirm-dialog ──── */

function CancelOrderBlock({ orderId, onDone }: { orderId: string; onDone: () => void }) {
  const [mode, setMode] = useState<'idle' | 'confirm'>('idle');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.cancelOrder(orderId, reason.trim() || undefined);
      setMode('idle');
      setReason('');
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось отменить заказ');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      <h4 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>Отмена заказа</h4>
      {mode === 'idle' ? (
        <>
          <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>
            Пока работа не началась, заказ можно отменить без последствий.
          </p>
          <div>
            <button className="btn btn-tertiary" onClick={() => { setMode('confirm'); setError(null); }}>
              Отменить заказ
            </button>
          </div>
        </>
      ) : (
        <>
          <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-primary)', fontWeight: 'var(--fw-medium)' }}>
            Отменить заказ? Это действие нельзя отменить.
          </p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Причина (необязательно)"
            rows={2}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-separator)',
              fontSize: 'var(--fs-body)',
              fontFamily: 'inherit',
              background: 'var(--color-surface)',
              color: 'var(--color-text-primary)',
              resize: 'vertical',
            }}
          />
          {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}
          <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
            <button
              className="btn"
              disabled={busy}
              onClick={() => void submit()}
              style={{ background: 'var(--color-danger)', color: '#FFFFFF' }}
            >
              {busy ? 'Отмена…' : 'Да, отменить заказ'}
            </button>
            <button className="btn btn-tertiary" disabled={busy} onClick={() => { setMode('idle'); setReason(''); setError(null); }}>
              Не отменять
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ── audit5-B1a (500f41b1): customer confirm-delivery with two-step
   confirm — the mirror of the cancel block for the happy end of the
   lifecycle. No free-text input: the action only records the handoff. */

function ConfirmDeliveryBlock({ orderId, onDone }: { orderId: string; onDone: () => void }) {
  const [mode, setMode] = useState<'idle' | 'confirm'>('idle');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.confirmDelivery(orderId);
      setMode('idle');
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось подтвердить получение');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      <h4 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>Получение заказа</h4>
      {mode === 'idle' ? (
        <>
          <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>
            Проверьте заказ при получении и подтвердите вручную — заказ завершится, и можно оставить отзыв.
          </p>
          <div>
            <button className="btn btn-primary" onClick={() => { setMode('confirm'); setError(null); }}>
              Подтвердить получение
            </button>
          </div>
        </>
      ) : (
        <>
          <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-primary)', fontWeight: 'var(--fw-medium)' }}>
            Заказ получен и всё в порядке? Заказ будет завершён, и вы сможете оставить отзыв.
          </p>
          {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}
          <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" disabled={busy} onClick={() => void submit()}>
              {busy ? 'Сохранение…' : 'Да, заказ получен'}
            </button>
            <button className="btn btn-tertiary" disabled={busy} onClick={() => { setMode('idle'); setError(null); }}>
              Ещё нет
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ── TASK-c82f2eb1 (audit-2 P4): review CTA on a Completed order ── */

function ReviewOrderBlock({ orderId }: { orderId: string }) {
  const [mode, setMode] = useState<'idle' | 'form'>('idle');
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      // Same path as ReviewsBlock (TASK-179) — rating 1–5 required, comment
      // optional ≤2000; the server binds the review to this order's
      // provider, so no orderId/providerId picker is needed here.
      await api.createReview(orderId, rating, comment.trim());
      setMode('idle');
      setComment('');
      setDone(true);
    } catch (e) {
      // Duplicate review or pool order — the server's own message is the
      // most honest copy; no engine changes from the Web side.
      setError(e instanceof Error ? e.message : 'Не удалось отправить отзыв');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="card" style={{ padding: 'var(--space-md)' }}>
        <span className="badge badge-success">✓ Спасибо! Отзыв опубликован.</span>
      </div>
    );
  }

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      <h4 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>
        Отзыв о заказе
      </h4>
      {mode === 'idle' ? (
        <>
          <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>
            Расскажите, как всё прошло — отзыв появится на странице исполнителя и поможет другим покупателям.
          </p>
          <div>
            <button className="btn btn-primary" onClick={() => { setMode('form'); setError(null); }}>
              Оставить отзыв
            </button>
          </div>
        </>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-xs)' }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                style={{
                  fontSize: 20,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: star <= rating ? 'var(--color-warning, #FF9500)' : 'var(--color-separator)',
                }}
                aria-label={`Оценка ${star}`}
              >
                ★
              </button>
            ))}
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Как всё прошло? (необязательно)"
            rows={3}
            maxLength={2000}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-separator)',
              fontSize: 'var(--fs-body)',
              fontFamily: 'inherit',
              background: 'var(--color-surface)',
              color: 'var(--color-text-primary)',
              resize: 'vertical',
            }}
          />
          {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}
          <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
            <button className="btn btn-primary" disabled={busy} onClick={() => void submit()}>
              {busy ? 'Отправка…' : 'Отправить отзыв'}
            </button>
            <button className="btn btn-tertiary" disabled={busy} onClick={() => { setMode('idle'); setComment(''); setError(null); }}>
              Отмена
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── 0aa086dd (audit-13 Card A): per-product review forms on a Completed order ── */

function ProductReviewOrderBlock({ orderId, items }: { orderId: string; items: OrderItem[] }) {
  const targets = productReviewTargets(items);
  if (targets.length === 0) return null;

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      <h4 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>
        Отзывы о товарах
      </h4>
      <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>
        Оцените купленные товары — отзывы появятся на страницах товаров и помогут другим покупателям.
      </p>
      {targets.map((t) => (
        <ProductReviewLine key={t.productId} orderId={orderId} target={t} />
      ))}
    </div>
  );
}

function ProductReviewLine({ orderId, target }: { orderId: string; target: ProductReviewTarget }) {
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      // Same contract as the provider review: rating 1–5 required, text
      // optional ≤2000; the server binds the review to this order line.
      await api.createProductReview(orderId, target.productId, rating, text.trim());
      setDone(true);
    } catch (e) {
      // Duplicate / gate rejections — the server's message is the honest copy.
      setError(e instanceof Error ? e.message : 'Не удалось отправить отзыв');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div style={{ borderTop: '1px solid var(--color-separator)', paddingTop: 'var(--space-md)' }}>
        <span style={{ fontWeight: 600 }}>{target.title}</span>
        <span className="badge badge-success" style={{ marginLeft: 'var(--space-sm)' }}>✓ Отзыв опубликован</span>
      </div>
    );
  }

  return (
    <div style={{ borderTop: '1px solid var(--color-separator)', paddingTop: 'var(--space-md)', display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
      <span style={{ fontWeight: 600 }}>{target.title}</span>
      <div style={{ display: 'flex', gap: 'var(--space-xs)' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => setRating(star)}
            disabled={busy}
            style={{
              fontSize: 20,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: star <= rating ? 'var(--color-warning, #FF9500)' : 'var(--color-separator)',
            }}
            aria-label={`Оценка ${star}`}
          >
            ★
          </button>
        ))}
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Что понравилось или что улучшить? (необязательно)"
        rows={2}
        maxLength={2000}
        disabled={busy}
        style={{
          width: '100%',
          padding: '12px 16px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-separator)',
          fontSize: 'var(--fs-body)',
          fontFamily: 'inherit',
          background: 'var(--color-surface)',
          color: 'var(--color-text-primary)',
          resize: 'vertical',
        }}
      />
      {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}
      <div>
        <button className="btn btn-primary" disabled={busy} onClick={() => void submit()}>
          {busy ? 'Отправка…' : 'Отправить отзыв о товаре'}
        </button>
      </div>
    </div>
  );
}

/* ── Confirm / dispute block ─────────────────────────────── */

/** dc25865a: dispute evidence cap (images only, backend-verified). */
const MAX_DISPUTE_PHOTOS = 3;

function ConfirmDisputeBlock({ orderId, disputeOnly = false, onDone }: { orderId: string; disputeOnly?: boolean; onDone: () => void }) {
  const [mode, setMode] = useState<'idle' | 'confirm' | 'dispute'>('idle');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  // dc25865a (audit-12 §5 Card A): dispute evidence staging — same picker
  // pattern as the order chat (≤3 photos, staged via the customer-scoped
  // attachments endpoint, thumbs with a remove button before submit).
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // TASK-250: failures render as a recovery affordance, not a dead-end
  // raw line — stale-state errors (P2 auto-confirm, status moved on)
  // get an «Обновить заказ» button that rides the existing load() path.
  const [failure, setFailure] = useState<OrderActionFailureView | null>(null);

  const attachFiles = async (files: FileList | null) => {
    if (files === null || files.length === 0 || uploading) return;
    const room = MAX_DISPUTE_PHOTOS - photos.length;
    if (room <= 0) return;
    setUploading(true);
    try {
      setFailure(null);
      const accepted = Array.from(files).slice(0, room);
      const urls: string[] = [];
      for (const file of accepted) {
        const uploaded = await api.uploadDisputeEvidencePhoto(orderId, file);
        urls.push(uploaded.url);
      }
      setPhotos((p) => [...p, ...urls].slice(0, MAX_DISPUTE_PHOTOS));
    } catch (e) {
      setFailure(plainActionFailure(e instanceof Error ? e.message : 'Не удалось загрузить фото'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const submit = async () => {
    setBusy(true);
    setFailure(null);
    try {
      if (mode === 'confirm') {
        await api.confirmOrderResult(orderId, comment.trim() || undefined);
      } else {
        const reason = comment.trim();
        if (!reason) {
          setFailure(plainActionFailure('Укажите причину спора'));
          setBusy(false);
          return;
        }
        await api.disputeOrderResult(orderId, reason, photos);
      }
      setMode('idle');
      setComment('');
      setPhotos([]);
      onDone();
    } catch (e) {
      setFailure(buildOrderActionFailure(mode === 'confirm' ? 'confirm' : 'dispute', e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      <h4 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>
        {disputeOnly ? 'Спор по отправленному заказу' : 'Подтверждение результата'}
      </h4>
      <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>
        {disputeOnly
          ? 'Если что-то не так с полученным заказом, вы можете открыть спор с причиной.'
          : 'Проверьте фото результата. После подтверждения исполнитель отправит заказ.'}
      </p>

      {mode === 'idle' ? (
        <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
          {!disputeOnly && (
            <button className="btn btn-primary" onClick={() => { setMode('confirm'); setFailure(null); }}>
              Подтвердить результат
            </button>
          )}
          <button className="btn btn-tertiary" onClick={() => { setMode('dispute'); setFailure(null); }}>
            Открыть спор
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={mode === 'confirm' ? 'Комментарий (необязательно)' : 'Причина спора (обязательно)'}
            rows={3}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-separator)',
              fontSize: 'var(--fs-body)',
              fontFamily: 'inherit',
              background: 'var(--color-surface)',
              color: 'var(--color-text-primary)',
              resize: 'vertical',
            }}
          />
          {mode === 'dispute' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
              {photos.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  {photos.map((url) => (
                    <div key={url} style={{ position: 'relative' }}>
                      <img
                        src={resolveMediaUrl(url)}
                        alt="Фото к спору"
                        style={{
                          width: 56,
                          height: 56,
                          objectFit: 'cover',
                          borderRadius: 10,
                          border: '1px solid var(--color-border, rgba(0,0,0,0.08))',
                          display: 'block',
                        }}
                      />
                      <button
                        type="button"
                        aria-label="Убрать фото"
                        disabled={busy}
                        onClick={() => setPhotos((p) => p.filter((u) => u !== url))}
                        style={{
                          position: 'absolute',
                          top: -6,
                          right: -6,
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          border: 'none',
                          background: 'var(--color-text-primary, #1C1C1E)',
                          color: '#fff',
                          fontSize: 12,
                          lineHeight: 1,
                          cursor: 'pointer',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => { void attachFiles(e.target.files); }}
                />
                <button
                  type="button"
                  className="btn btn-tertiary"
                  style={{ height: 36, padding: '6px 12px', fontSize: 'var(--fs-caption)' }}
                  title="Прикрепить фото доказательств (до 3)"
                  disabled={photos.length >= MAX_DISPUTE_PHOTOS || uploading || busy}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? 'Загрузка…' : '📷 Фото доказательств'}
                </button>
              </div>
            </div>
          )}
          {failure && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)', alignItems: 'flex-start' }}>
              <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)', margin: 0 }}>{failure.message}</p>
              {/* TASK-250: stale-state failures recover by refreshing the
                  order — onDone is the same load() the success path uses,
                  and the fresh view model drops this block on its own. */}
              {failure.recovery === 'refresh' && (
                <button className="btn btn-tertiary" disabled={busy} onClick={() => { setMode('idle'); onDone(); }}>
                  {failure.recoveryLabel}
                </button>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
            <button
              className="btn"
              disabled={busy}
              onClick={() => void submit()}
              style={mode === 'confirm' ? undefined : { background: 'var(--color-danger)', color: '#FFFFFF' }}
            >
              {busy ? 'Отправка…' : mode === 'confirm' ? 'Подтвердить' : 'Отправить спор'}
            </button>
            <button className="btn btn-tertiary" disabled={busy} onClick={() => { setMode('idle'); setComment(''); setPhotos([]); setFailure(null); }}>
              Отмена
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── TASK-252: view-model presenters ─────────────────────── */

const BANNER_TONE_COLOR = {
  accent: 'var(--color-primary)',
  neutral: 'var(--color-text-secondary)',
  success: 'var(--color-success)',
  danger: 'var(--color-danger)',
} as const;

/* SD-3 (d36195ae): Safe Deal pay surface on the order detail. The paid
   branch mirrors BannerCard's success shape (no action once the escrow
   hold exists); the awaiting branch renders the pay/retry CTA that calls
   POST /orders/{id}/pay and redirects to the returned YooKassa
   confirmation URL. Light theme, Montserrat, iOS-18 minimal per
   DESIGN-SYSTEM.md — card + accent CTA, no new primitives. */
function OrderPayBlock({
  orderId,
  card,
  onChanged,
}: {
  orderId: string;
  card: OrderPayCard;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (card.ctaLabel === null) {
    return (
      <div
        className="card"
        style={{
          padding: 'var(--space-md)',
          display: 'flex',
          gap: 'var(--space-sm)',
          alignItems: 'flex-start',
          border: '1px solid var(--color-success)',
        }}
      >
        <span style={{ fontSize: 'var(--fs-h3)', color: 'var(--color-success)', fontWeight: 'var(--fw-bold)', lineHeight: 1.2 }}>
          ✓
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
          <p style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-primary)' }}>
            {card.title}
          </p>
          <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
            {card.text}
          </p>
        </div>
      </div>
    );
  }

  const pay = async () => {
    setBusy(true);
    setError(null);
    try {
      // Redirect to the YooKassa confirmation page; return_url brings the
      // customer back to the web. No URL = the rail hiccuped — surface an
      // in-band error, the CTA doubles as the retry.
      const p = await api.payOrder(orderId);
      if (p.confirmationUrl) {
        window.location.assign(p.confirmationUrl);
        return;
      }
      setError('Платёжная ссылка недоступна — попробуйте позже.');
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось создать платёж — попробуйте ещё раз');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="card"
      style={{
        padding: 'var(--space-md)',
        display: 'flex',
        gap: 'var(--space-sm)',
        alignItems: 'flex-start',
        border: '1px solid var(--color-primary)',
      }}
    >
      <span style={{ fontSize: 'var(--fs-h3)', color: 'var(--color-primary)', fontWeight: 'var(--fw-bold)', lineHeight: 1.2 }}>
        ›
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)', flex: 1 }}>
        <p style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-primary)' }}>
          {card.title}
        </p>
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          {card.text}
        </p>
        {error && (
          <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>
        )}
        <div>
          <button className="btn btn-primary" disabled={busy} onClick={() => void pay()}>
            {busy ? 'Создаём платёж…' : card.ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function BannerCard({ banner }: { banner: CustomerBanner }) {
  const color = BANNER_TONE_COLOR[banner.tone];
  return (
    <div
      className="card"
      style={{
        padding: 'var(--space-md)',
        display: 'flex',
        gap: 'var(--space-sm)',
        alignItems: 'flex-start',
        border: `1px solid ${banner.tone === 'neutral' ? 'var(--color-separator)' : color}`,
      }}
    >
      <span style={{ fontSize: 'var(--fs-h3)', color, fontWeight: 'var(--fw-bold)', lineHeight: 1.2 }}>
        {banner.tone === 'success' ? '✓' : banner.tone === 'danger' ? '!' : banner.tone === 'accent' ? '›' : '·'}
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
        <p style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-primary)' }}>
          {banner.title}
        </p>
        {banner.text && (
          <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
            {banner.text}
          </p>
        )}
      </div>
    </div>
  );
}

/* TASK-252: shared photo tile — caption + real date under the preview.
   TASK-256: flexible width — two per row on desktop, one on phones.
   TASK-b2e2cd38: the tile is a real button opening the full-size viewer —
   cropped 180px grid previews are not enough to judge print quality. */
function PhotoTile({
  photo,
  alt,
  onOpen,
}: {
  photo: { url: string; caption: string | null; createdAtUtc: string };
  alt: string;
  onOpen: () => void;
}) {
  return (
    <figure style={{ flex: '1 1 140px', maxWidth: 180, display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
      <button type="button" className="photo-tile-btn" aria-label={`${alt}: открыть полностью`} onClick={onOpen}>
        <img
          src={resolveMediaUrl(photo.url)}
          alt={photo.caption ?? alt}
          loading="lazy"
          style={{
            width: '100%',
            aspectRatio: '1 / 1',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      </button>
      {(photo.caption || photo.createdAtUtc) && (
        <figcaption style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          {photo.caption && <span>{photo.caption}</span>}
          {photo.caption && photo.createdAtUtc && <span> · </span>}
          {photo.createdAtUtc && (
            <span>{new Date(photo.createdAtUtc).toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
          )}
        </figcaption>
      )}
    </figure>
  );
}

/* TASK-b2e2cd38: full-size photo viewer for the customer confirmation flow.
   Light-theme overlay per DESIGN-SYSTEM (rgba(0,0,0,0.4) scrim, radius-lg,
   --shadow-modal): contained (uncropped) image, caption + timestamp, ‹ ›
   navigation within the opened group, Esc/backdrop close, scroll lock.
   TASK-896c4009 (B6-M1): horizontal pointer swipe on the contained image
   (≥40px, horizontally dominant) also steps the group via onStep —
   keyboard/buttons stay authoritative; pointer capture keeps a swipe
   release from ever becoming a backdrop close.
   TASK-80c601c8 (B6 a11y): focus moves into the dialog on open and back
   to the trigger on close, Tab wraps inside the aria-modal dialog, and
   the caption/counter column is a polite live region (role=status). */
const PHOTO_SWIPE_THRESHOLD_PX = 40;

function PhotoLightbox({
  photos,
  index,
  onClose,
  onStep,
}: {
  photos: CustomerPhotoView[];
  index: number;
  onClose: () => void;
  onStep: (delta: number) => void;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onStep(-1);
      else if (e.key === 'ArrowRight') onStep(1);
      else if (e.key === 'Tab') {
        /* TASK-80c601c8: keep Tab inside the aria-modal dialog — the only
           focusables here are the ‹/›/✕ buttons (1–3 of them); wrap from
           first→last and back so focus never escapes behind the scrim. */
        const items = rootRef.current?.querySelectorAll<HTMLButtonElement>('button:not([disabled])');
        if (!items || items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && (active === first || active === rootRef.current)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (active === last || active === rootRef.current)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onStep]);

  /* TASK-80c601c8 (B6 a11y): move focus into the dialog on open (the
     scrim-covered trigger keeps DOM focus otherwise) and return it to the
     trigger element on close — without restore, keyboard/SR users land on
     <body> and lose their place in the photo grid. */
  useEffect(() => {
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    rootRef.current?.focus();
    return () => {
      restoreFocusRef.current?.focus();
    };
  }, []);

  const photo = photos[index];
  const multi = photos.length > 1;

  /* TASK-896c4009: swipe gesture — only meaningful for multi-photo groups;
     single-photo mounts skip the handlers entirely. */
  const swipeStart = useRef<{ x: number; y: number; id: number } | null>(null);

  const onSwipeStart = (e: ReactPointerEvent<HTMLImageElement>) => {
    if (!multi) return;
    swipeStart.current = { x: e.clientX, y: e.clientY, id: e.pointerId };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* capture is best-effort; without it a swipe that leaves the image
         simply does not step */
    }
  };

  const onSwipeEnd = (e: ReactPointerEvent<HTMLImageElement>) => {
    const start = swipeStart.current;
    swipeStart.current = null;
    if (!multi || !start || start.id !== e.pointerId) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) >= PHOTO_SWIPE_THRESHOLD_PX && Math.abs(dx) > Math.abs(dy)) {
      onStep(dx < 0 ? 1 : -1);
    }
  };

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label={photo.caption ?? 'Просмотр фото'}
      tabIndex={-1}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-md)',
        padding: 'var(--space-lg)',
        cursor: 'zoom-out',
      }}
    >
      <img
        src={resolveMediaUrl(photo.url)}
        alt={photo.caption ?? 'Фото'}
        draggable={false}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={onSwipeStart}
        onPointerUp={onSwipeEnd}
        onPointerCancel={() => { swipeStart.current = null; }}
        style={{
          maxWidth: 'min(960px, 94vw)',
          maxHeight: '76vh',
          objectFit: 'contain',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--color-surface)',
          boxShadow: 'var(--shadow-modal)',
          cursor: 'default',
          touchAction: 'none',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
      />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-md)',
          flexWrap: 'wrap',
          justifyContent: 'center',
          cursor: 'default',
        }}
      >
        {multi && (
          <button type="button" className="btn btn-tertiary" aria-label="Предыдущее фото" onClick={() => onStep(-1)}>
            ‹
          </button>
        )}
        <div
          role="status"
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-xxs)' }}
        >
          {photo.caption && (
            <span
              style={{
                fontSize: 'var(--fs-body)',
                fontWeight: 'var(--fw-medium)',
                background: 'var(--color-surface)',
                borderRadius: 'var(--radius-pill)',
                padding: '6px 16px',
                boxShadow: 'var(--shadow-floating)',
              }}
            >
              {photo.caption}
            </span>
          )}
          <span
            style={{
              fontSize: 'var(--fs-caption)',
              color: 'var(--color-text-secondary)',
              background: 'var(--color-surface)',
              borderRadius: 'var(--radius-pill)',
              padding: '4px 12px',
              boxShadow: 'var(--shadow-floating)',
            }}
          >
            {multi ? `${index + 1} / ${photos.length} · ` : ''}
            {new Date(photo.createdAtUtc).toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
        {multi && (
          <button type="button" className="btn btn-tertiary" aria-label="Следующее фото" onClick={() => onStep(1)}>
            ›
          </button>
        )}
      </div>
      <button
        type="button"
        className="btn btn-tertiary"
        aria-label="Закрыть просмотр"
        onClick={onClose}
        style={{ position: 'absolute', top: 'var(--space-md)', right: 'var(--space-md)' }}
      >
        ✕
      </button>
    </div>
  );
}

/* ── Helpers ──────────────────────────────────────────────── */
/* normalizeOrderId moved to ordersFocusView (d17ac4ce) — the
    comparison is unit-tested alongside the focus resolution. */

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-md)', fontSize: 'var(--fs-body)' }}>
      <span style={{ color: 'var(--color-text-secondary)' }}>{label}</span>
      <span style={{ textAlign: 'right', fontWeight: 'var(--fw-medium)' }}>{value}</span>
    </div>
  );
}
